<script>
/**
 * @component UrgencyWaitingRoom
 * @description Sala de espera unificada de la cola de urgencias (UC-REG-282 +
 * UC-REG-283). Única pantalla del paciente: llega por el link que recibe al
 * entrar a la fila (`/urgencia/:queueId/:patientId`, sin login, identidad
 * por URL como la videoconsulta) y no la abandona.
 *
 *   - Mientras espera: cuántas personas tiene adelante, espera estimada,
 *     cronómetro; sube documentos para que el médico los vea desde el primer
 *     segundo; prueba cámara y micrófono (deja los permisos aprobados); token
 *     de validación; "Salir de la fila". Manda un heartbeat de presencia para
 *     que el médico lo vea "en línea" en la cola.
 *   - Alerta sonora cuando queda 1 persona adelante y cuando el médico lo toma.
 *   - Cuando el médico toma el caso, el panel izquierdo pasa SOLO a la
 *     videollamada (auto-ingreso con el mismo componente Twilio que la
 *     videoconsulta), sin redirección ni botón; queda un botón de respaldo si
 *     el navegador bloquea cámara/mic. Los documentos subidos antes siguen
 *     visibles (se copiaron al turno) y se pueden seguir subiendo.
 */
import Twilio from "@/components/video-room/Twilio.vue";
import urgencyQueueService from "@/services/urgency-queue";
import appointmentService from "@/services/appointment";
import videoRoomService from "@/services/videoRoom";
import {
  formatAhead,
  formatEta,
  playChime,
  transitionAlerts,
} from "@/services/urgency-waiting-room";

/** @description Intervalo de polling del estado (ms) */
const POLL_MS = 10000;
/** @description Intervalo del heartbeat de presencia (ms) */
const PRESENCE_MS = 20000;

export default {
  name: "UrgencyWaitingRoom",

  components: { Twilio },

  data() {
    return {
      /** @type {string} loading | ready | not-found */
      loadState: "loading",
      /** @type {Object|null} Última respuesta de /public/:id/status */
      status: null,
      /** @type {Array} Documentos (del caso mientras espera, del turno después) */
      attachments: [],
      isUploading: false,
      /** @type {string} Token de validación — visible, sin wiring (igual que la videoconsulta) */
      token: "",
      cancelling: false,
      /** @type {boolean} Sonido habilitado (requiere un gesto del usuario) */
      soundEnabled: false,
      /** @type {AudioContext|null} */
      audioCtx: null,
      /** @type {string} idle | testing | ok | error */
      cameraState: "idle",
      /** @type {MediaStream|null} Previsualización de la cámara */
      previewStream: null,
      /** @type {string|null} Access token de Twilio */
      accessToken: null,
      isConnecting: false,
      /** @type {boolean} El auto-ingreso falló (cámara bloqueada, etc.) */
      joinFailed: false,
      pollTimer: null,
      presenceTimer: null,
      tickTimer: null,
      /** @type {number} Reloj para el cronómetro de espera */
      now: Date.now(),
    };
  },

  computed: {
    /** @returns {string} ID del caso desde la URL */
    queueId() {
      return this.$route.params.queueId;
    },
    /** @returns {string} ID del paciente desde la URL */
    patientId() {
      return this.$route.params.patientId;
    },
    /** @returns {boolean} */
    isWaiting() {
      return !!this.status && this.status.status === "waiting";
    },
    /** @returns {boolean} */
    isInProgress() {
      return !!this.status && this.status.status === "in-progress";
    },
    /** @returns {boolean} */
    isClosed() {
      return (
        !!this.status && ["completed", "cancelled"].includes(this.status.status)
      );
    },
    /** @returns {boolean} Mostrar la videollamada en vivo */
    inCall() {
      return (
        this.isInProgress && !!this.accessToken && !!this.status.videoRoomId
      );
    },
    /** @returns {string} "Hay N personas antes que vos" */
    aheadText() {
      return this.status ? formatAhead(this.status.ahead) : "";
    },
    /** @returns {string} Estimación en texto */
    etaText() {
      return this.status ? formatEta(this.status.estimatedWait) : "—";
    },
    /** @returns {string} Especialidad de la guardia */
    specialtyName() {
      return (this.status && this.status.specialty) || "urgencias";
    },
    /** @returns {string} Nombre del médico con prefijo según género */
    doctorName() {
      const d = this.status && this.status.doctor;
      if (!d) return "";
      const prefix =
        d.gender === "f" ? "Dra." : d.gender === "m" ? "Dr." : "Dr/a.";
      return `${prefix} ${d.firstName || ""} ${d.lastName || ""}`.trim();
    },
    /** @returns {string} Iniciales para el avatar (médico si ya lo tomó, si no la guardia) */
    avatarInitials() {
      const d = this.status && this.status.doctor;
      if (d) {
        return (
          ((d.firstName || "")[0] || "") + ((d.lastName || "")[0] || "")
        ).toUpperCase();
      }
      return "G";
    },
    /** @returns {string} Tiempo que lleva esperando */
    elapsed() {
      if (!this.status || !this.status.requestedAt) return "";
      const mins = Math.max(
        0,
        Math.floor(
          (this.now - new Date(this.status.requestedAt).getTime()) / 60000
        )
      );
      return mins < 60
        ? `${mins} min`
        : `${Math.floor(mins / 60)} h ${mins % 60} min`;
    },
  },

  async created() {
    await this.refresh();
    this.pollTimer = setInterval(this.refresh, POLL_MS);
    this.presenceTimer = setInterval(this.markPresence, PRESENCE_MS);
    this.tickTimer = setInterval(() => {
      this.now = Date.now();
    }, 30000);
    this.markPresence();
  },

  beforeDestroy() {
    [this.pollTimer, this.presenceTimer, this.tickTimer].forEach(
      (t) => t && clearInterval(t)
    );
    this.stopPreview();
  },

  methods: {
    /**
     * @description Carga el estado y reacciona a los cambios: alertas
     *              sonoras y auto-ingreso a la videollamada.
     * @returns {Promise<void>}
     */
    async refresh() {
      let next;
      try {
        next = await urgencyQueueService.publicStatus(
          this.queueId,
          this.patientId
        );
      } catch (e) {
        if (this.loadState === "loading") this.loadState = "not-found";
        return; // un error de red se reintenta en el próximo ciclo
      }
      const prev = this.status;
      this.status = next;
      this.loadState = "ready";
      if (next.status === "waiting") this.attachments = next.attachments || [];

      transitionAlerts(prev, next).forEach((kind) => this.chime(kind));

      if (next.status === "in-progress") {
        await this.syncAppointment();
        if (!this.accessToken && !this.isConnecting && !this.joinFailed) {
          await this.connect();
        }
      }
    },

    /**
     * @description Con el caso tomado: documentos del turno (los de la sala
     *              ya se copiaron) y presencia en el turno para que el médico
     *              vea "En sala" en Atenciones.
     * @returns {Promise<void>}
     */
    async syncAppointment() {
      if (!this.status.appointmentId) return;
      try {
        const landing = await appointmentService.getVideoConsultationLanding(
          this.status.appointmentId,
          this.patientId
        );
        this.attachments = landing.attachments || this.attachments;
      } catch {
        // best-effort
      }
      try {
        await appointmentService.markVideoPresence(
          this.status.appointmentId,
          this.patientId
        );
      } catch {
        // best-effort
      }
    },

    /**
     * @description Entra a la videollamada: token de Twilio para la sala
     *              del turno. Si falla, queda el botón de respaldo.
     * @returns {Promise<void>}
     */
    async connect() {
      if (!this.status || !this.status.videoRoomId) return;
      this.isConnecting = true;
      this.stopPreview();
      try {
        this.accessToken = await videoRoomService.getPatientRoomAccessToken(
          this.status.videoRoomId,
          this.patientId
        );
        this.joinFailed = false;
      } catch {
        this.joinFailed = true;
      }
      this.isConnecting = false;
    },

    /**
     * @description Reintento manual del ingreso (botón de respaldo).
     * @returns {Promise<void>}
     */
    async retryJoin() {
      this.joinFailed = false;
      this.enableSound();
      await this.connect();
    },

    /**
     * @description Heartbeat de presencia mientras espera (best-effort).
     * @returns {Promise<void>}
     */
    async markPresence() {
      if (!this.isWaiting && this.loadState !== "loading") return;
      try {
        await urgencyQueueService.publicPresence(this.queueId, this.patientId);
      } catch {
        // best-effort
      }
    },

    /**
     * @description Habilita el sonido (necesita un gesto del usuario para
     *              crear el AudioContext). Lo llaman el botón de sonido y la
     *              prueba de cámara.
     */
    enableSound() {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!this.audioCtx) this.audioCtx = new Ctx();
        if (this.audioCtx.state === "suspended") this.audioCtx.resume();
        this.soundEnabled = true;
      } catch {
        this.soundEnabled = false;
      }
    },

    /**
     * @description Suena una alerta si el sonido está habilitado.
     * @param {'next'|'taken'} kind
     */
    chime(kind) {
      if (!this.soundEnabled) return;
      try {
        playChime(this.audioCtx, kind);
      } catch {
        // sin audio
      }
    },

    /**
     * @description Prueba cámara y micrófono: deja los permisos aprobados y
     *              muestra la previsualización. También habilita el sonido
     *              (es un gesto del usuario).
     * @returns {Promise<void>}
     */
    async testCamera() {
      this.enableSound();
      this.cameraState = "testing";
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        this.previewStream = stream;
        this.cameraState = "ok";
        this.$nextTick(() => {
          if (this.$refs.preview) this.$refs.preview.srcObject = stream;
        });
      } catch {
        this.cameraState = "error";
      }
    },

    /** @description Apaga la previsualización (libera la cámara antes de entrar a la llamada). */
    stopPreview() {
      if (this.previewStream) {
        this.previewStream.getTracks().forEach((t) => t.stop());
        this.previewStream = null;
      }
      if (this.cameraState === "ok") this.cameraState = "idle";
    },

    /** @description Abre el selector de archivos. */
    triggerFilePicker() {
      this.$refs.fileInput.click();
    },

    /**
     * @description Sube el archivo elegido: al caso mientras espera, al turno
     *              una vez tomado.
     * @param {Event} e - change del input file
     */
    async onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      this.isUploading = true;
      try {
        const res = this.status.appointmentId
          ? await appointmentService.uploadAppointmentAttachment(
              this.status.appointmentId,
              this.patientId,
              file
            )
          : await urgencyQueueService.publicUploadAttachment(
              this.queueId,
              this.patientId,
              file
            );
        this.attachments = res.attachments || [];
      } catch {
        this.$message.error("No se pudo subir el documento.");
      }
      this.isUploading = false;
    },

    /**
     * @description Quita un documento.
     * @param {string} attachmentId
     */
    async removeAttachment(attachmentId) {
      try {
        const res = this.status.appointmentId
          ? await appointmentService.deleteAppointmentAttachment(
              this.status.appointmentId,
              attachmentId,
              this.patientId
            )
          : await urgencyQueueService.publicDeleteAttachment(
              this.queueId,
              attachmentId,
              this.patientId
            );
        this.attachments = res.attachments || [];
      } catch {
        this.$message.error("No se pudo quitar el documento.");
      }
    },

    /**
     * @description Formatea el tamaño de un archivo.
     * @param {number} bytes
     * @returns {string}
     */
    formatSize(bytes) {
      if (!bytes) return "";
      const mb = bytes / (1024 * 1024);
      if (mb >= 1) return `${mb.toFixed(1)} MB`;
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    },

    /**
     * @description El paciente se baja de la fila. Pide confirmación.
     * @returns {Promise<void>}
     */
    async cancel() {
      try {
        await this.$confirm(
          "Vas a salir de la fila de la guardia. Si después necesitás atención, vas a tener que volver a pedirla.",
          "¿Salir de la fila?",
          {
            confirmButtonText: "Salir de la fila",
            cancelButtonText: "Seguir esperando",
            type: "warning",
          }
        );
      } catch {
        return;
      }
      this.cancelling = true;
      try {
        await urgencyQueueService.publicCancel(this.queueId, this.patientId);
        await this.refresh();
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.error) ||
          "No se pudo salir de la fila";
        this.$message.error(msg);
      } finally {
        this.cancelling = false;
      }
    },
  },
};
</script>

<template lang="pug">
.uwr
  header.uwr-top
    .uwr-logo
      span Quantux&nbsp;
      b Salud

  //- Cargando
  .uwr-center(v-if="loadState === 'loading'")
    .uwr-spinner
    p Buscando tu lugar en la fila...

  //- Enlace inválido
  .uwr-center(v-else-if="loadState === 'not-found'")
    .uwr-nf-icon
      i.el-icon-warning-outline
    h2 No encontramos tu solicitud
    p El enlace no es válido o la solicitud ya no está disponible.

  main.uwr-layout(v-else)
    //- ═══ Stage: espera / videollamada ═══
    section.uwr-stage(:class="{ 'uwr-stage--call': inCall }")
      Twilio(
        v-if="inCall"
        :patient-id="patientId"
        :appointment-id="status.appointmentId"
        :identity="patientId"
        :external-token="accessToken"
        :external-room-id="status.videoRoomId"
        :autoJoin="true"
      )

      //- En espera
      .uwr-wait(v-else-if="isWaiting")
        .uwr-wait-badge
          i.el-icon-time
        p.uwr-wait-title {{ aheadText }}
        .uwr-stats
          .uwr-stat
            span.uwr-stat-label Espera estimada
            span.uwr-stat-value {{ etaText }}
          .uwr-stat
            span.uwr-stat-label En la fila hace
            span.uwr-stat-value {{ elapsed }}
        p.uwr-wait-sub
          | Dejá esta pantalla abierta: cuando el médico te tome, entrás a la videollamada solo.
          | Mientras esperás, podés subir documentos y probar tu cámara.
        span.uwr-pulse
          span.uwr-dot
          | Esperando al médico
        button.uwr-sound(v-if="!soundEnabled" @click="enableSound")
          i.el-icon-bell
          span Activar alertas sonoras
        span.uwr-sound.uwr-sound--on(v-else)
          i.el-icon-bell
          span Alertas sonoras activadas

      //- Tomado, conectando (o respaldo si falló)
      .uwr-wait(v-else-if="isInProgress")
        .uwr-wait-badge.uwr-wait-badge--ok
          i.el-icon-video-camera
        p.uwr-wait-title {{ doctorName || 'El médico' }} te está esperando
        template(v-if="joinFailed")
          p.uwr-wait-sub No pudimos entrar solos a la videollamada. Tocá el botón y permití el acceso a cámara y micrófono.
          button.uwr-cta(@click="retryJoin")
            i.el-icon-video-camera
            span Entrar a la consulta
        template(v-else)
          p.uwr-wait-sub Conectándote a la videollamada...
          .uwr-spinner.uwr-spinner--inline

      //- Cerrado
      .uwr-wait(v-else)
        .uwr-wait-badge.uwr-wait-badge--done
          i.el-icon-check
        p.uwr-wait-title {{ status.status === 'cancelled' ? 'Saliste de la fila' : 'Tu consulta ya terminó' }}
        p.uwr-wait-sub(v-if="status.status === 'cancelled'") Si necesitás atención, pedí una nueva consulta de guardia.
        p.uwr-wait-sub(v-else) Gracias por usar la guardia virtual.

    //- ═══ Sidebar: preparación ═══
    aside.uwr-side
      .uwr-appt
        .uwr-avatar {{ avatarInitials }}
        .uwr-appt-info
          span.uwr-kind Guardia de {{ specialtyName }}
          h1.uwr-doctor {{ doctorName || 'Videoconsulta' }}
          span.uwr-meta(v-if="isWaiting") {{ aheadText }}
          span.uwr-meta(v-else-if="isInProgress") En atención

      section.uwr-block(v-if="!isClosed")
        .uwr-block-head
          i.el-icon-paperclip
          h2 Documentos para la consulta
        p.uwr-block-desc Subí estudios, recetas o cualquier información que quieras que el médico revise. Los va a ver apenas te tome.
        button.uwr-attach(@click="triggerFilePicker" :disabled="isUploading")
          | {{ isUploading ? "Subiendo..." : "+ Adjuntar documento" }}
        input.uwr-file-input(ref="fileInput" type="file" @change="onFileSelected")
        .uwr-file(v-for="f in attachments" :key="f._id")
          .uwr-file-ic
            i.el-icon-document
          .uwr-file-meta
            span.uwr-file-name {{ f.name }}
            span.uwr-file-size {{ formatSize(f.size) }}
          span.uwr-file-x(@click="removeAttachment(f._id)")
            i.el-icon-close

      section.uwr-block(v-if="isWaiting")
        .uwr-block-head
          i.el-icon-camera
          h2 Cámara y micrófono
        p.uwr-block-desc Probalos ahora así, cuando el médico te tome, entrás sin tener que aprobar nada.
        video.uwr-preview(v-show="cameraState === 'ok'" ref="preview" autoplay muted playsinline)
        p.uwr-camera-ok(v-if="cameraState === 'ok'")
          i.el-icon-success
          |  Todo listo
        p.uwr-camera-error(v-else-if="cameraState === 'error'") No pudimos acceder a la cámara o al micrófono. Revisá los permisos del navegador.
        button.uwr-attach(v-if="cameraState !== 'ok'" @click="testCamera" :disabled="cameraState === 'testing'")
          | {{ cameraState === 'testing' ? "Probando..." : "Probar cámara y micrófono" }}
        button.uwr-attach(v-else @click="stopPreview") Apagar la cámara hasta que me toquen

      section.uwr-block(v-if="!isClosed")
        .uwr-block-head
          h2 Token de validación
          span.uwr-opt si tu consulta lo requiere
        input.uwr-token(v-model="token" type="text" placeholder="Ingresá el código")

      .uwr-cta-wrap(v-if="isWaiting")
        button.uwr-cancel(@click="cancel" :disabled="cancelling")
          i.el-icon-close
          span {{ cancelling ? "Saliendo..." : "Salir de la fila" }}
        p.uwr-cta-hint Te avisamos por WhatsApp y por mail cuando seas el próximo y cuando el médico te llame.
</template>

<style lang="scss" scoped>
$navy: #002d5e;
$teal: #05e5ba;
$teal-dark: #0d9d9d;
$bg: #eef2f7;
$text: #16243b;
$muted: #6e7c90;
$line: #e7ecf2;

.uwr {
  min-height: 100vh;
  background: $bg;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: $text;
}

.uwr-top {
  background: #fff;
  border-bottom: 1px solid $line;
  padding: 14px 24px;
  display: flex;
  align-items: center;
}
.uwr-logo {
  font-size: 16px;
  color: $navy;
  b {
    font-weight: 800;
  }
  span {
    font-weight: 700;
  }
}

/* ---- Estados loading / not-found ---- */
.uwr-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 24px;
  color: $muted;

  h2 {
    color: $navy;
    font-size: 20px;
    margin: 16px 0 8px;
  }
  p {
    font-size: 14px;
  }
}
.uwr-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #dde4ec;
  border-top-color: $teal;
  border-radius: 50%;
  animation: uwr-spin 0.8s linear infinite;
  margin-bottom: 16px;

  &--inline {
    margin: 18px auto 0;
  }
}
@keyframes uwr-spin {
  to {
    transform: rotate(360deg);
  }
}
.uwr-nf-icon {
  font-size: 46px;
  color: #e0a92e;
}

/* ---- Layout 2 columnas ---- */
.uwr-layout {
  max-width: 1140px;
  margin: 0 auto;
  padding: 24px 16px 40px;
  display: flex;
  gap: 20px;
  align-items: stretch;
}

/* ---- Stage ---- */
.uwr-stage {
  flex: 1.7;
  background: #f4f7fb;
  border: 1px solid $line;
  border-radius: 18px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;

  &--call {
    padding: 0;
    overflow: hidden;
    background: #000;
  }
}
::v-deep .uwr-stage--call .video-chat {
  height: 100%;
  min-height: 480px;
}
.uwr-wait {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
}
.uwr-wait-badge {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fff3da;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  font-size: 32px;
  color: #e0a92e;

  &--ok {
    background: #dcfce7;
    color: #16a34a;
  }
  &--done {
    background: $line;
    color: $muted;
  }
}
.uwr-wait-title {
  font-size: 22px;
  font-weight: 800;
  color: $navy;
  margin: 0;
  line-height: 1.2;
}
.uwr-wait-sub {
  font-size: 14px;
  color: $muted;
  margin-top: 10px;
  line-height: 1.55;
  max-width: 380px;
}
.uwr-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
  margin-top: 16px;
}
.uwr-stat {
  background: #fff;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &-label {
    font-size: 11px;
    font-weight: 600;
    color: $muted;
  }
  &-value {
    font-size: 20px;
    font-weight: 800;
    color: $navy;
  }
}
.uwr-pulse {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 18px;
  font-size: 13px;
  font-weight: 600;
  color: #b7791f;
  background: #fff7e6;
  padding: 7px 14px;
  border-radius: 20px;
}
.uwr-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0a92e;
}
.uwr-sound {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $navy;
  background: #fff;
  border: 1px solid $line;
  border-radius: 20px;
  padding: 6px 12px;
  cursor: pointer;

  &--on {
    color: #16a34a;
    cursor: default;
  }
}
.uwr-cta {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $navy;
  color: #fff;
  border: none;
  font-weight: 700;
  font-size: 15px;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
}

/* ---- Sidebar ---- */
.uwr-side {
  width: 372px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 6px 28px rgba(0, 45, 94, 0.08);
  display: flex;
  flex-direction: column;
}
.uwr-appt {
  display: flex;
  gap: 13px;
  align-items: center;
  padding: 22px 22px 18px;
}
.uwr-avatar {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #05e5ba, #00b48f);
  color: $navy;
  font-weight: 800;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.uwr-appt-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.uwr-kind {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #00806a;
  background: #e6fcf6;
  padding: 2px 8px;
  border-radius: 6px;
  width: fit-content;
}
.uwr-doctor {
  font-size: 17px;
  font-weight: 800;
  color: $navy;
  margin: 4px 0 0;
}
.uwr-meta {
  font-size: 13px;
  color: $muted;
}

.uwr-block {
  border-top: 1px solid $line;
  padding: 18px 22px;
}
.uwr-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  i {
    color: $navy;
  }
  h2 {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }
}
.uwr-opt {
  margin-left: auto;
  font-size: 11px;
  color: $muted;
}
.uwr-block-desc {
  font-size: 13px;
  color: $muted;
  line-height: 1.5;
  margin: 0 0 12px;
}
.uwr-attach {
  width: 100%;
  border: 1px dashed #c7d2e0;
  background: #fff;
  border-radius: 10px;
  padding: 11px;
  font-size: 13px;
  font-weight: 600;
  color: $navy;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: $teal-dark;
    background: #f9fefe;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  + .uwr-attach {
    margin-top: 8px;
  }
}
.uwr-file-input {
  display: none;
}
.uwr-file {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid $line;
  border-radius: 10px;
}
.uwr-file-ic {
  color: $navy;
}
.uwr-file-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.uwr-file-name {
  font-size: 13px;
  font-weight: 600;
  color: $text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uwr-file-size {
  font-size: 11px;
  color: $muted;
}
.uwr-file-x {
  color: $muted;
  cursor: pointer;
  &:hover {
    color: #dc2626;
  }
}
.uwr-preview {
  width: 100%;
  border-radius: 10px;
  background: #000;
  margin-bottom: 10px;
  max-height: 180px;
  object-fit: cover;
}
.uwr-camera-ok {
  font-size: 13px;
  font-weight: 600;
  color: #16a34a;
  margin: 0 0 10px;
}
.uwr-camera-error {
  font-size: 12px;
  color: #dc2626;
  margin: 0 0 10px;
}
.uwr-token {
  width: 100%;
  border: 1px solid $line;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: $teal-dark;
  }
}
.uwr-cta-wrap {
  border-top: 1px solid $line;
  padding: 18px 22px 22px;
  text-align: center;
}
.uwr-cancel {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 10px;
  padding: 11px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
.uwr-cta-hint {
  font-size: 12px;
  color: $muted;
  margin: 10px 0 0;
  line-height: 1.5;
}

@media (max-width: 860px) {
  .uwr-layout {
    flex-direction: column;
  }
  .uwr-side {
    width: 100%;
  }
  .uwr-stage {
    min-height: 360px;
    padding: 24px;
  }
}
</style>
